
import { motion } from "framer-motion";
import { User, LogOut, Loader2, Crown, Shield } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";

/**
 * MobileMenuUser Component
 * 
 * User profile card and sign-out button for authenticated users.
 * Phase 3: Enhanced with improved avatar, role badges, and loading states
 * 
 * Features:
 * - Large, prominent avatar with gradient background
 * - User metadata support (full name, avatar URL)
 * - Admin role badge with crown icon
 * - Animated gradient background
 * - Loading state during sign-out
 * - Enhanced sign-out button with destructive styling
 * - Display name extraction from email or metadata
 * 
 * @param {MobileMenuUserProps} props - Component props
 * @returns {JSX.Element} User profile section
 */

interface AuthUser {
  email?: string | null;
  role?: string;
  user_metadata?: {
    full_name?: string;
    avatar_url?: string;
  };
}

interface MobileMenuUserProps {
  user: AuthUser;
  handleSignOut: () => void;
}

const MobileMenuUser = ({ user, handleSignOut }: MobileMenuUserProps) => {
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleSignOutClick = async () => {
    setIsSigningOut(true);
    await handleSignOut();
    // No need to set false as component will unmount
  };

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  const displayName = user.user_metadata?.full_name || user.email?.split('@')[0] || 'User';
  const isAdmin = user.role === 'admin';

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
    >
      {/* Enhanced User Profile Card */}
      <motion.div 
        className="relative p-3 mb-4 rounded-2xl bg-gradient-to-br from-primary/15 dark:from-primary/10 via-secondary/10 dark:via-secondary/8 to-accent/10 border border-primary/30 dark:border-primary/20 backdrop-blur-sm overflow-hidden group"
        whileHover={{ scale: 1.01 }}
        transition={{ duration: 0.2 }}
      >
        {/* Animated background gradient */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          animate={{
            background: [
              "linear-gradient(135deg, hsl(var(--primary)/0.05) 0%, transparent 100%)",
              "linear-gradient(225deg, hsl(var(--secondary)/0.05) 0%, transparent 100%)",
              "linear-gradient(135deg, hsl(var(--primary)/0.05) 0%, transparent 100%)",
            ],
          }}
          transition={{ duration: 5, repeat: Infinity }}
        />

        <div className="relative flex items-center gap-3">
          {/* Enhanced Avatar */}
          <motion.div 
            className="relative"
            whileHover={{ scale: 1.05, rotate: [0, -5, 5, 0] }}
            transition={{ duration: 0.3 }}
          >
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary via-primary/80 to-secondary text-primary-foreground flex items-center justify-center text-base font-bold border-2 border-background shadow-lg">
              {user.user_metadata?.avatar_url ? (
                <img 
                  src={user.user_metadata.avatar_url} 
                  alt="Avatar" 
                  className="h-full w-full rounded-xl object-cover"
                />
              ) : (
                <User className="h-6 w-6" />
              )}
            </div>
          </motion.div>

          {/* User Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <p className="font-bold text-sm truncate text-foreground">
                {displayName}
              </p>
              {isAdmin && (
                <Badge className="bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/30 text-[10px] font-semibold px-2 py-0">
                  <Crown className="h-2.5 w-2.5 mr-1" />
                  Admin
                </Badge>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground/95 truncate">{user.email}</p>
          </div>
        </div>
      </motion.div>

      {/* Enhanced Sign Out Button */}
      <motion.button 
        onClick={handleSignOutClick}
        disabled={isSigningOut}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.98 }}
        className="w-full flex items-center justify-center min-h-[44px] px-4 py-2.5 rounded-2xl text-sm font-semibold text-destructive hover:text-destructive-foreground bg-destructive/10 hover:bg-destructive border border-destructive/30 hover:border-destructive transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg"
      >
        {isSigningOut ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            <span>Signing out...</span>
          </>
        ) : (
          <>
            <LogOut className="mr-2 h-4 w-4 group-hover:scale-110 transition-transform duration-200" />
            <span>Sign Out</span>
          </>
        )}
      </motion.button>
    </motion.div>
  );
};

export default MobileMenuUser;
